import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Ruler,
  FileArrowUp,
  DownloadSimple,
  Trash,
  ArrowSquareOut,
  LinkSimple,
  Stack,
  PenNib,
  DeviceMobile,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useDirectory } from "@/lib/directory";
import { useAuth } from "@/context/AuthContext";

const prettySize = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

export const TemplatorCard = ({ jobId, job, onChange }) => {
  const { nameOf } = useDirectory();
  const [url, setUrl] = useState(job.templator_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [textPhone, setTextPhone] = useState("");
  const [texting, setTexting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef(null);
  const token = localStorage.getItem("sw_token");
  const tpl = job.templator_template;
  const savedUrl = job.templator_url || "";
  const quotedSqft = Number(tpl?.quoted_sqft) || 0;
  const fieldSqft = Number(tpl?.total_sqft) || 0;
  const sqftDiff =
    quotedSqft > 0 ? Math.round((fieldSqft - quotedSqft) * 100) / 100 : null;
  const sqftMatches =
    sqftDiff !== null && Math.abs(sqftDiff) <= Math.max(0.5, quotedSqft * 0.02);

  const pushToDesign = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/templator/push-to-design`);
      toast.success(
        `${data.pieces} pieces sent to ${data.quote_code || "the quote"} — opening Design Studio`,
      );
      navigate(`/quotes/${data.quote_id}/design?tab=draw`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not open Design Studio",
      );
    }
    setPushing(false);
  };

  const textLink = async () => {
    const to = textPhone.trim();
    if (to.length < 7) {
      toast.error("Enter a mobile number");
      return;
    }
    setTexting(true);
    try {
      await api.post("/sms/send", {
        to,
        body: `Brioni Granite — LPI Cloud template for ${job.project_name || "your job"}: ${savedUrl}`,
        entity: "job",
        entity_id: jobId,
      });
      toast.success(`Template link texted to ${to}`);
      setTextOpen(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send text",
      );
    }
    setTexting(false);
  };

  const saveLink = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/jobs/${jobId}/templator/link`, { url });
      onChange(data);
      setUrl(data.templator_url || "");
      toast.success(data.templator_url ? "Templator link saved" : "Templator link cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save link");
    }
    setSaving(false);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/jobs/${jobId}/templator/dxf`, fd);
      onChange(data);
      toast.success(
        `Template imported — ${data.templator_template?.piece_count || 0} pieces`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeTemplate = async () => {
    try {
      const { data } = await api.delete(`/jobs/${jobId}/templator/dxf`);
      onChange(data);
      toast.success("Template removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <div
      className="rounded-lg border border-indigo-500/25 bg-indigo-500/5 p-4"
      data-testid="job-templator-card"
    >
      <div className="flex items-center gap-2">
        <Ruler size={18} weight="bold" className="text-indigo-300" />
        <div className="text-xs font-semibold uppercase tracking-widest text-indigo-200">
          Templator / LPI Cloud
        </div>
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Link the digital template job and import the DXF exported from LPI Cloud.
      </p>

      {/* LPI Cloud link */}
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">
          LPI Cloud / Templator link
        </div>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <LinkSimple
              size={15}
              weight="bold"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid="templator-link-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://templator.com/… or LPI Cloud job link"
              className="h-10 border-zinc-700 bg-zinc-950 pl-9 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="templator-link-save"
              onClick={saveLink}
              disabled={saving || url === savedUrl}
              className="h-10 bg-indigo-500 text-xs font-semibold uppercase tracking-wider text-white hover:bg-indigo-400"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            {savedUrl && (
              <a
                href={savedUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="templator-link-open"
                className="flex h-10 items-center gap-2 rounded-md border border-zinc-700 px-3 text-xs font-semibold uppercase tracking-wider text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
              >
                <ArrowSquareOut size={15} weight="bold" />
                Open
              </a>
            )}
            {savedUrl && (
              <button
                type="button"
                data-testid="templator-text-toggle"
                onClick={() => {
                  setTextPhone(textPhone || user?.phone || "");
                  setTextOpen((v) => !v);
                }}
                className="flex h-10 items-center gap-2 rounded-md border border-zinc-700 px-3 text-xs font-semibold uppercase tracking-wider text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
              >
                <DeviceMobile size={15} weight="bold" />
                Text
              </button>
            )}
          </div>
        </div>
        {textOpen && savedUrl && (
          <div
            className="mt-2 flex flex-col gap-2 sm:flex-row"
            data-testid="templator-text-row"
          >
            <Input
              data-testid="templator-text-phone"
              value={textPhone}
              onChange={(e) => setTextPhone(e.target.value)}
              placeholder="Crew mobile number"
              className="h-10 flex-1 border-zinc-700 bg-zinc-950 text-sm"
            />
            <Button
              data-testid="templator-text-send"
              onClick={textLink}
              disabled={texting}
              className="h-10 bg-indigo-500 text-xs font-semibold uppercase tracking-wider text-white hover:bg-indigo-400"
            >
              <PaperPlaneTilt size={15} weight="bold" className="mr-2" />
              {texting ? "Sending…" : "Text link"}
            </Button>
          </div>
        )}
      </div>

      {/* DXF template */}
      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">
            DXF template
          </div>
          <Button
            data-testid="templator-dxf-upload"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="h-9 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
          >
            <FileArrowUp size={15} weight="bold" className="mr-2" />
            {uploading ? "Importing…" : tpl ? "Replace DXF" : "Import DXF"}
          </Button>
          <input
            ref={inputRef}
            data-testid="templator-dxf-input"
            type="file"
            accept=".dxf,.xdf"
            onChange={upload}
            className="hidden"
          />
        </div>

        {!tpl ? (
          <p
            data-testid="templator-empty"
            className="mt-3 rounded-md border border-dashed border-zinc-800 bg-zinc-950/60 p-3 text-sm text-zinc-500"
          >
            No template imported yet — export the job from LPI Cloud as DXF and import it here.
          </p>
        ) : (
          <div
            data-testid="templator-template-summary"
            className="mt-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-zinc-100">
                  {tpl.original_filename}
                </div>
                <div className="font-mono-data mt-0.5 text-xs text-zinc-500">
                  {prettySize(tpl.size)} · {nameOf(tpl.uploaded_by)}
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <a
                  href={`${API}/jobs/${jobId}/templator/dxf?auth=${token}`}
                  data-testid="templator-dxf-download"
                  className="rounded-md p-2 text-zinc-400 transition-colors duration-150 hover:bg-zinc-800 hover:text-indigo-300"
                  title="Download DXF"
                >
                  <DownloadSimple size={18} weight="bold" />
                </a>
                <button
                  type="button"
                  data-testid="templator-dxf-remove"
                  onClick={removeTemplate}
                  title="Remove template"
                  className="rounded-md p-2 text-zinc-500 transition-colors duration-150 hover:bg-red-950/40 hover:text-red-400"
                >
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-md border border-zinc-800 bg-zinc-900 p-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500">
                  <Stack size={13} weight="bold" /> Pieces
                </div>
                <div
                  data-testid="templator-piece-count"
                  className="font-mono-data mt-1 text-lg font-semibold text-zinc-100"
                >
                  {tpl.piece_count}
                </div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-900 p-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500">
                  <Ruler size={13} weight="bold" /> Total area
                </div>
                <div
                  data-testid="templator-total-sqft"
                  className="font-mono-data mt-1 text-lg font-semibold text-zinc-100"
                >
                  {tpl.total_sqft} ft²
                </div>
              </div>
            </div>

            {quotedSqft > 0 && (
              <div
                data-testid="templator-diff"
                className={`mt-3 rounded-md border px-3 py-2 text-xs ${
                  sqftMatches
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-300"
                }`}
              >
                {sqftMatches ? (
                  <>Field template matches the quoted area (~{quotedSqft} ft²).</>
                ) : (
                  <>
                    <span className="font-semibold">Measurements changed:</span>{" "}
                    field template {fieldSqft} ft² vs quoted {quotedSqft} ft² —{" "}
                    {sqftDiff > 0 ? "+" : ""}
                    {sqftDiff} ft². Review before cutting.
                  </>
                )}
              </div>
            )}

            {tpl.pieces?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="templator-piece-list">
                {tpl.pieces.map((p, i) => (
                  <div
                    key={i}
                    data-testid={`templator-piece-${i}`}
                    className="flex items-center justify-between rounded-md bg-zinc-900 px-3 py-1.5 text-xs"
                  >
                    <span className="text-zinc-300">{p.label || `Piece ${i + 1}`}</span>
                    <span className="font-mono-data text-zinc-500">
                      {p.length_in}" × {p.width_in}" · {p.sqft} ft²
                      {p.cutouts ? ` · ${p.cutouts} cut-out${p.cutouts > 1 ? "s" : ""}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              data-testid="templator-push-design"
              onClick={pushToDesign}
              disabled={pushing}
              className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-indigo-500 text-xs font-semibold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-50"
            >
              <PenNib size={15} weight="bold" />
              {pushing ? "Opening Design Studio…" : "Edit in Design Studio"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
